import type { AitypesInsight } from '@polaris/api-client'
import {
  FeaturedInsightCard,
  FeaturedInsightCardSkeleton,
} from './featured-insight-card'
import { InsightCard, InsightCardSkeleton } from './insight-card'

interface InsightsGridProps {
  insights: AitypesInsight[]
  isLoading: boolean
}

export function InsightsGrid({ insights, isLoading }: InsightsGridProps) {
  const [featuredInsight, ...restInsights] = insights

  const nonFeaturedInsights = restInsights.slice(0, 6)

  // Show loading when we're loading OR when we have no data yet
  // This prevents the flicker of "No insights found" during initial load
  const shouldShowLoading = isLoading || insights.length === 0

  // Only show "No insights found" when we're certain data has loaded and is empty
  const shouldShowNoData =
    !isLoading && insights !== null && insights.length === 0

  if (shouldShowNoData) {
    return (
      <div className='max-w-4xl mx-auto w-full flex flex-col justify-center items-center gap-4 py-28 min-h-[648px]'>
        <h1 className='text-primary text-4xl font-serif font-extralight max-w-96'>
          No insights found.
        </h1>
      </div>
    )
  }

  return (
    <>
      <div className='max-w-4xl mx-auto mt-2'>
        {shouldShowLoading || !featuredInsight ? (
          <FeaturedInsightCardSkeleton />
        ) : (
          <FeaturedInsightCard insight={featuredInsight} />
        )}
      </div>
      <div className='max-w-4xl mx-auto mt-2 pt-12'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-8 -mx-2'>
          {shouldShowLoading || !restInsights.length
            ? Array.from({ length: 6 }).map((_, i) => (
                <InsightCardSkeleton key={i} />
              ))
            : nonFeaturedInsights.map((insight, index) => (
                <InsightCard key={insight.id} insight={insight} index={index} />
              ))}
        </div>
      </div>
    </>
  )
}
